import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { setToastErrorFn } from '../utils/errorHandlers';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextType {
  toasts: Toast[];
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  removeToast: (id: string) => void;
}

const DURATIONS: Record<ToastType, number> = {
  success: 4000,
  error: 8000,
  warning: 5000,
  info: 5000,
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = crypto.randomUUID();
    const toast: Toast = {
      id,
      message,
      type,
      duration: DURATIONS[type],
    };
    setToasts(prev => [toast, ...prev]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((message: string) => addToast('success', message), [addToast]);
  const showError = useCallback((message: string) => addToast('error', message), [addToast]);
  const showWarning = useCallback((message: string) => addToast('warning', message), [addToast]);
  const showInfo = useCallback((message: string) => addToast('info', message), [addToast]);

  // Register showError with errorHandlers for global error display
  useEffect(() => {
    setToastErrorFn(showError);
    return () => {
      // Clear toast function on unmount
      setToastErrorFn(null);
    };
  }, [showError]);

  const value: ToastContextType = {
    toasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeToast,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};
